import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import ThemeToggle from "@/components/theme-toggle";

const signInMethods = [
  { id: "telegram", label: "Telegram", icon: "📱" },
  { id: "paypal", label: "PayPal", icon: "💳" },
  { id: "crypto", label: "Crypto", icon: "🔐" },
];

export default function SignIn() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    setTimeout(() => {
      toast.success("Signed in successfully!");
      setFormData({ email: "", password: "", rememberMe: false });
      setIsSubmitting(false);
    }, 1500);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <a href="/" className="font-semibold text-lg tracking-tight hover:opacity-70 transition-opacity">
            MuskConnect Pro
          </a>
          <ThemeToggle />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 flex items-center justify-center min-h-[calc(100vh-80px)]">
        <motion.div
          className="w-full max-w-md"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="mb-12">
            <h1 className="text-4xl font-medium tracking-tight mb-3">Welcome Back</h1>
            <p className="text-lg text-muted-foreground">
              Sign in to access your account and community features.
            </p>
          </motion.div>

          <motion.form onSubmit={handleSubmit} className="space-y-6" variants={containerVariants} initial="hidden" animate="visible">
            {/* Quick Sign In Methods */}
            <motion.div variants={itemVariants}>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
                Sign in with
              </p>
              <div className="grid grid-cols-3 gap-3">
                {signInMethods.map((m) => (
                  <motion.button
                    key={m.id}
                    type="button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-4 border border-border hover:border-foreground transition-colors text-center"
                  >
                    <div className="text-xl mb-1">{m.icon}</div>
                    <p className="text-xs font-medium">{m.label}</p>
                  </motion.button>
                ))}
              </div>
            </motion.div>

            {/* Divider */}
            <motion.div variants={itemVariants} className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-background text-muted-foreground">Or with email</span>
              </div>
            </motion.div>

            {/* Email */}
            <motion.div variants={itemVariants}>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3 block">
                Email Address
              </label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-3 px-0"
              />
            </motion.div>

            {/* Password */}
            <motion.div variants={itemVariants}>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
                  Password
                </label>
                <a href="#" className="text-xs text-foreground hover:underline">
                  Forgot?
                </a>
              </div>
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                className="bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-3 px-0"
              />
            </motion.div>

            {/* Remember Me */}
            <motion.div variants={itemVariants} className="flex items-center gap-2">
              <input
                type="checkbox"
                name="rememberMe"
                id="remember"
                checked={formData.rememberMe}
                onChange={handleChange}
              />
              <label htmlFor="remember" className="text-sm text-muted-foreground">
                Remember me for 30 days
              </label>
            </motion.div>

            {/* Submit Button */}
            <motion.div variants={itemVariants}>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50 font-medium py-6 h-auto"
                >
                  {isSubmitting ? "Signing in..." : "Sign In"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.div>
            </motion.div>

            {/* Sign Up Link */}
            <motion.p variants={itemVariants} className="text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <a href="/signup" className="text-foreground font-medium hover:underline">
                Create one
              </a>
            </motion.p>
          </motion.form>

          {/* Security Note */}
          <motion.div
            variants={itemVariants}
            className="mt-12 pt-8 border-t border-border text-center"
          >
            <p className="text-xs text-muted-foreground">
              We use industry-standard encryption to protect your data. Your security is our priority.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
